// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Simulation events — the one-way channel from physics to rules.
//
// The simulation never knows what a score is. It reports that a switch closed, a target
// dropped, a spinner turned once more. The rule engine reads that list and decides what it
// means. That separation is exactly the switch-and-coil boundary of a real machine
// (04 §9), and it is what lets a machine definition stay data rather than code.
//
// Events are appended in a fixed order — devices by index, then balls by index — so a
// replay re-simulated on another runtime produces the identical list.

/** Event type discriminants. Integers rather than strings, so the C# core ports one-to-one. */
export const EV_SWITCH_CLOSED = 0
export const EV_SWITCH_OPENED = 1
export const EV_SPINNER_REVOLUTION = 2
export const EV_TARGET_DOWN = 3
export const EV_TARGET_UP = 4
export const EV_BANK_COMPLETE = 5
export const EV_BALL_CAPTURED = 6
export const EV_BALL_EJECTED = 7
export const EV_BUMPER_HIT = 8
export const EV_SLINGSHOT_FIRED = 9
export const EV_GATE_PASSED = 10

export const EVENT_NAMES: readonly string[] = [
  'switchClosed',
  'switchOpened',
  'spinnerRevolution',
  'targetDown',
  'targetUp',
  'bankComplete',
  'ballCaptured',
  'ballEjected',
  'bumperHit',
  'slingshotFired',
  'gatePassed',
]

export interface SimEvent {
  readonly type: number
  /** Index into `World.devices`, or −1 for an event no device owns. */
  readonly device: number
  /** Index of the ball responsible, or −1. */
  readonly ball: number
  /** The step on which it happened. The simulation's only notion of time (PH-086). */
  readonly step: number
  /**
   * Type-dependent magnitude. Impact speed for a hit, exit speed for an eject, the signed
   * direction for a spinner revolution. Drives sound gain and haptic strength, so it must
   * come out of the simulation rather than be guessed by the renderer.
   */
  readonly value: number
}

/**
 * A second, non-consuming reader of the queue.
 *
 * The rule engine *drains* the queue every step, so by the time a renderer running at sixty
 * frames a second looks, sixteen steps of events have already been taken and thrown away.
 * That is why `SimEvent.value` — an impact speed the physics computes precisely so that sound
 * gain, camera shake and haptic strength do not have to be guessed — reached no presentation
 * code at all: there was no way for presentation to see an event.
 *
 * Primitives rather than a `SimEvent`, so a tap costs no allocation and cannot mutate an event
 * the rule engine has not read yet. Reading only: a tap that wrote to the simulation would put
 * the renderer inside the determinism boundary, and a replay validated on a headless server
 * installs no tap at all.
 */
export type EventTap = (
  type: number,
  device: number,
  ball: number,
  step: number,
  value: number,
) => void

/**
 * An append-only queue drained once per frame by the rule engine.
 *
 * Events are rare — a busy multiball produces perhaps fifty a second against a million
 * simulation steps — so allocating one small object each is not worth avoiding, and a
 * plain array keeps the ordering obvious.
 */
export class EventQueue {
  private events: SimEvent[] = []

  /**
   * Installed by the presentation layer, absent everywhere else.
   *
   * Undefined during replay validation, during the soak and in every core test, so the branch
   * below is the whole cost of having it.
   */
  tap: EventTap | undefined = undefined

  emit(type: number, device: number, ball: number, step: number, value: number): void {
    this.events.push({ type, device, ball, step, value })
    if (this.tap !== undefined) this.tap(type, device, ball, step, value)
  }

  get length(): number {
    return this.events.length
  }

  /** Read without consuming. For tests and diagnostics. */
  peek(): readonly SimEvent[] {
    return this.events
  }

  /** Take everything and reset. The caller owns the returned array. */
  drain(): SimEvent[] {
    const out = this.events
    this.events = []
    return out
  }

  clear(): void {
    this.events.length = 0
  }
}

/** Human-readable form, for diagnostics and test failures. Never called in the hot path. */
export function describeEvent(e: SimEvent): string {
  const name = EVENT_NAMES[e.type] ?? `event${e.type}`
  return `${name}(device=${e.device} ball=${e.ball} step=${e.step} value=${e.value.toFixed(3)})`
}
